import { Tabs } from "@ark-ui/solid";
import type { Component, JSX, ParentComponent } from "solid-js";
import { Show, createMemo, For, untrack } from "solid-js";
import { isEmpty } from "lodash-es";
import { Key } from "@solid-primitives/keyed";

export type TabItem = {
  label: () => string;
  key: string;
  classes?: string;
  visible?: boolean;
  Slot: Component;
};

type GlobalTabsProps = {
  items: TabItem[];
  initialTab?: string;
  value?: () => string | undefined;
  onTabChange?: (tabKey: string) => void;
  tabStyle?: string;
  testId?: string;
  fallbackIfEmpty?: JSX.Element;
};

const GlobalTabs: ParentComponent<GlobalTabsProps> = (props) => {
  const filteredItems = createMemo(() => props.items.filter(({ visible }) => visible ?? true));
  return (
    <Show when={!isEmpty(filteredItems())} fallback={props.fallbackIfEmpty}>
      <Tabs.Root
        value={props.value?.()}
        defaultValue={props.initialTab ?? filteredItems()[0].key}
        onValueChange={({ value }) => props.onTabChange?.(value)}
        class="flex size-full min-h-0 flex-col"
        orientation="horizontal"
        data-testId={props.testId}
      >
        <Tabs.List>
          <div
            class="flex flex-wrap gap-2 border-b border-subtle text-center text-base-medium text-button-text-tertiary-soft"
            classList={{ [props.tabStyle ?? ""]: true }}
          >
            <For each={filteredItems()}>
              {(item) => (
                <Tabs.Trigger
                  class="translate-y-px border-b-2 border-transparent p-2 pb-1.5 hover:text-button-text-tertiary-soft-hover focus-visible:outline-default selected:border-brand selected:text-button-text-tertiary-active"
                  value={item.key}
                >
                  {item.label()}
                </Tabs.Trigger>
              )}
            </For>
          </div>
        </Tabs.List>
        <Key each={filteredItems()} by="key">
          {(item) => {
            // Not tracked: `Slot` is a new function on every rebuild, which would remount the tab
            const Slot = untrack(() => item().Slot);
            return (
              <Tabs.Content
                class="-mx-px h-full min-h-0 overflow-y-auto px-px"
                classList={{ [item().classes ?? ""]: true }}
                value={item().key}
              >
                <Slot />
              </Tabs.Content>
            );
          }}
        </Key>
      </Tabs.Root>
    </Show>
  );
};

export default GlobalTabs;
